// FROM: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/FieldLocatorSignature/index.tsx

/* eslint-disable react/require-default-props,react/default-props-match-prop-types */
import { Flex, Text } from '@modusign/design-system';
import { SignatureType } from '@modusign/react-signing/types';
import { useMemo, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import FieldLocatorBadge from '@app/components/FieldLocatorBadge';
import SignatureTypeSelectList from '@app/components/FieldLocatorSignature/SignatureTypeSelectList';
import SignatureFieldDropdownButton from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/SignatureFieldDropdownButton';
import orderRotationForColor from '@app/libs/participantFields/orderRotationForColor';

interface FieldLocatorSignatureProps {
  allowSignatureTypes: SignatureType[];
  count: number;
  order: number;
  readOnly: boolean;
  onClick: () => void;
  onSelectAllowSignatureType: (allowSignatureTypes: SignatureType[]) => void;
}

function convertAllowSignatureTypeToDomainType(
  type: 'SIGN' | 'STAMP' | 'SIGN_OR_STAMP'
): SignatureType[] {
  switch (type) {
    case 'SIGN':
      return ['SIGN'];
    case 'STAMP':
      return ['STAMP'];
    case 'SIGN_OR_STAMP':
    default:
      return ['SIGN', 'STAMP'];
  }
}

function FieldLocatorSignature({
  allowSignatureTypes,
  count,
  order,
  readOnly,
  onClick,
  onSelectAllowSignatureType,
}: FieldLocatorSignatureProps) {
  const [toggle, setToggle] = useState(false);

  const buttonText = useMemo(() => {
    if (allowSignatureTypes.includes('SIGN') && allowSignatureTypes.includes('STAMP')) {
      return '사인·도장';
    }

    if (allowSignatureTypes.includes('SIGN')) {
      return '사인';
    }

    if (allowSignatureTypes.includes('STAMP')) {
      return '도장';
    }

    return '사인·도장';
  }, [allowSignatureTypes]);

  function handleToggle(value = !toggle) {
    setToggle(value);
  }

  function handleSelect(type: string) {
    switch (type) {
      case 'SIGN':
        onSelectAllowSignatureType(convertAllowSignatureTypeToDomainType('SIGN'));
        break;
      case 'STAMP':
        onSelectAllowSignatureType(convertAllowSignatureTypeToDomainType('STAMP'));
        break;
      case 'SIGN_OR_STAMP':
      default:
        onSelectAllowSignatureType(convertAllowSignatureTypeToDomainType('SIGN_OR_STAMP'));
    }
    setToggle(false);
  }

  if (readOnly) {
    return (
      <Flex
        alignItems="center"
        as="button"
        bg="white"
        height="36px"
        justifyContent="space-between"
        mb={2}
        pb="5px"
        sx={{
          outline: 'none',
          '&:hover': {
            bg: 'lightGray50',
          },
        }}
        textAlign="left"
        type="button"
        width="100%"
      >
        <Text as="span" color="textNeutralLight" fontSize="md" fontWeight="medium" mr={3}>
          {buttonText}
        </Text>
        {count > 0 && <FieldLocatorBadge>{count}</FieldLocatorBadge>}
      </Flex>
    );
  }

  const rotatingOrder = orderRotationForColor(order);

  return (
    <OutsideClickHandler disabled={!toggle} onOutsideClick={() => handleToggle(false)}>
      <SignatureFieldDropdownButton
        allowSignatureTypes={allowSignatureTypes}
        count={count}
        order={rotatingOrder}
        onClick={onClick}
        onToggle={handleToggle}
      />
      {toggle && <SignatureTypeSelectList order={rotatingOrder} onSelect={handleSelect} />}
    </OutsideClickHandler>
  );
}

export default FieldLocatorSignature;
